import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { softDelete } from '../utils/softDelete';
import { useEsAdmin } from '../UserContext';
import { emitTareaChanged } from '../utils/tareasBus';

const prioridadColor = (p) => ({ urgente: 'var(--red)', normal: 'var(--blue)', baja: 'var(--text4)' }[p] || 'var(--blue)');
const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : null;

export default function TareaDetalle({ tareaId, onClose, onCompletar, onEdit, onNavigateExpediente, onNavigateTramite, grupos = [], onCambiarGrupo }) {
  const esAdmin = useEsAdmin();
  const [tarea, setTarea] = useState(null);
  const [notas, setNotas] = useState([]);
  const [nuevaNota, setNuevaNota] = useState('');
  const [completando, setCompletando] = useState(false);
  const [modalGrupoOpen, setModalGrupoOpen] = useState(false);
  const [nuevoGrupoInput, setNuevoGrupoInput] = useState('');
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (tareaId) { fetchTarea(); fetchNotas(); }
  }, [tareaId]);

  async function fetchTarea() {
    const { data } = await supabase
      .from('tareas')
      .select('*, expedientes(id, caratula, numero), tramites(id, nombre)')
      .eq('id', tareaId)
      .single();
    setTarea(data);
  }

  async function fetchNotas() {
    const { data } = await supabase
      .from('tareas_notas').select('*')
      .eq('tarea_id', tareaId)
      .order('created_at', { ascending: false });
    setNotas(data || []);
  }

  async function addNota() {
    if (!nuevaNota.trim()) return;
    await supabase.from('tareas_notas').insert({ tarea_id: tareaId, contenido: nuevaNota.trim() });
    setNuevaNota('');
    fetchNotas();
  }

  async function deleteNota(id) {
    await softDelete('tareas_notas', id);
    setNotas(ns => ns.filter(n => n.id !== id));
  }

  function handleAgregarGrupo(grupo) {
    setTarea(prev => ({ ...prev, grupo: grupo || null }));
    onCambiarGrupo?.(grupo || null);
    setModalGrupoOpen(false);
    setNuevoGrupoInput('');
    onClose();
  }

  async function handleCompletar() {
    setCompletando(true);
    await supabase.from('tareas').update({ completada: true, completada_en: new Date().toISOString() }).eq('id', tareaId);
    emitTareaChanged({ id: tareaId, completada: true });
    onCompletar?.(tareaId);
    onClose();
  }

  if (!tarea) return (
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ width: 520, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
        <span style={{ color: 'var(--text4)', fontSize: 13 }}>Cargando...</span>
      </div>
    </div>
  );

  const vencida = tarea.fecha_vencimiento && tarea.fecha_vencimiento < today && !tarea.completada;

  // Estado principal para el badge superior
  const estadoLabel = tarea.completada ? 'Completada' : tarea.descartada ? 'Descartada' : 'Pendiente';
  const estadoBg = tarea.completada ? '#dcfce7' : tarea.descartada ? 'var(--surface2)' : 'var(--surface2)';
  const estadoBorder = tarea.completada ? '#bbf7d0' : 'var(--border)';
  const estadoDot = tarea.completada ? '#16a34a' : tarea.descartada ? '#94a3b8' : '#94a3b8';
  const estadoColor = tarea.completada ? '#15803d' : '#64748b';

  return (
    <>
    <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ width: 520, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            <div style={{ width: 4, height: 28, borderRadius: 2, background: prioridadColor(tarea.prioridad), flexShrink: 0 }} />
            <div className="modal-title" style={{ fontSize: 15 }}>{tarea.titulo}</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>

          {/* Badge estado principal */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
            padding: '12px 16px', borderRadius: 10,
            background: estadoBg, border: `1px solid ${estadoBorder}`,
          }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: estadoDot }} />
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: estadoColor }}>
              {tarea.completada ? '✓ ' : ''}{estadoLabel}
            </span>
            {tarea.prioridad === 'urgente' && !tarea.completada && !tarea.descartada && (
              <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: 0.5 }}>· URGENTE</span>
            )}
            {tarea.fijada && !tarea.descartada && (
              <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 600, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: 0.5 }}>· 📌 Fijada</span>
            )}
            {tarea.grupo && (
              <span style={{ marginLeft: 'auto', fontSize: 11, background: '#f3e8ff', color: '#7c3aed', borderRadius: 10, padding: '1px 8px', fontWeight: 600 }}>📁 {tarea.grupo}</span>
            )}
          </div>

          {/* Descripción */}
          {tarea.descripcion && (
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 14, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, whiteSpace: 'pre-wrap' }}>
              {tarea.descripcion}
            </div>
          )}

          {/* Badges de fechas */}
          {(tarea.fecha || tarea.fecha_vencimiento || tarea.completada_en) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              {tarea.fecha && (
                <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: '#eff6ff', color: '#3b82f6', border: '0.5px solid #bfdbfe', fontWeight: 500 }}>
                  Agendado para el día {formatDate(tarea.fecha)}
                </span>
              )}
              {tarea.fecha_vencimiento && (
                <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: vencida ? '#fee2e2' : '#fefce8', color: vencida ? '#dc2626' : '#ca8a04', border: `0.5px solid ${vencida ? '#fecaca' : '#fde68a'}`, fontWeight: 500 }}>
                  Vencimiento {formatDate(tarea.fecha_vencimiento)}{vencida ? ' — VENCIDA' : ''}
                </span>
              )}
              {tarea.completada_en && (
                <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, background: '#dcfce7', color: '#16a34a', border: '0.5px solid #bbf7d0', fontWeight: 500, fontFamily: 'DM Mono' }}>
                  ✓ {new Date(tarea.completada_en).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          )}

          {/* Expediente / Trámite */}
          {tarea.expedientes && (
            <div
              onClick={() => { onNavigateExpediente?.(tarea.expedientes.id); onClose(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--blue-light)', border: '0.5px solid #bfdbfe', borderRadius: 8, marginBottom: 10, cursor: onNavigateExpediente ? 'pointer' : 'default' }}
            >
              <span style={{ fontSize: 16 }}>📁</span>
              <div>
                <div style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Expediente</div>
                <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>
                  {tarea.expedientes.numero ? `${tarea.expedientes.numero} — ` : ''}{tarea.expedientes.caratula}
                </div>
              </div>
              {onNavigateExpediente && <span style={{ marginLeft: 'auto', color: 'var(--blue)', fontSize: 16 }}>→</span>}
            </div>
          )}
          {tarea.tramites && (
            <div
              onClick={() => { onNavigateTramite?.(tarea.tramites.id); onClose(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#f3e8ff', border: '0.5px solid #ddd6fe', borderRadius: 8, marginBottom: 10, cursor: onNavigateTramite ? 'pointer' : 'default' }}
            >
              <span style={{ fontSize: 16 }}>📋</span>
              <div>
                <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Trámite</div>
                <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{tarea.tramites.nombre}</div>
              </div>
              {onNavigateTramite && <span style={{ marginLeft: 'auto', color: '#7c3aed', fontSize: 16 }}>→</span>}
            </div>
          )}

          {/* Notas */}
          <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Notas</div>
            <div style={{ marginBottom: 10, position: 'relative', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
              <textarea
                className="form-textarea"
                value={nuevaNota}
                onChange={e => setNuevaNota(e.target.value)}
                placeholder="Agregar una nota..."
                style={{ minHeight: 70, width: '100%', border: 'none', resize: 'none', background: 'transparent', display: 'block', paddingBottom: 38 }}
              />
              <button
                onClick={addNota}
                disabled={!nuevaNota.trim()}
                style={{ position: 'absolute', bottom: 8, right: 8, background: nuevaNota.trim() ? 'var(--blue)' : 'var(--text4)', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 12, fontWeight: 500, cursor: nuevaNota.trim() ? 'pointer' : 'default' }}
              >
                Agregar
              </button>
            </div>
            {notas.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text4)', textAlign: 'center', padding: '10px 0' }}>Sin notas aún</div>
            ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface2)', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {notas.map(n => (
                  <div key={n.id} style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 7, padding: '8px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 10, color: 'var(--text4)', fontFamily: 'DM Mono' }}>
                        {new Date(n.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {esAdmin && <button className="btn btn-danger btn-xs" style={{ padding: '0px 5px' }} onClick={() => deleteNota(n.id)}>🗑</button>}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{n.contenido}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
            {onCambiarGrupo && !tarea.completada && !tarea.descartada && (
              <button
                className="btn btn-ghost"
                onClick={() => { setNuevoGrupoInput(''); setModalGrupoOpen(true); }}
              >
                📁 {tarea.grupo ? 'Cambiar grupo' : 'Agregar a grupo'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {onEdit && !tarea.completada && !tarea.descartada && (
              <button className="btn btn-ghost" onClick={() => { onEdit(tarea); onClose(); }}>Editar</button>
            )}
            {!tarea.completada && !tarea.descartada && (
              <button className="btn btn-primary" onClick={handleCompletar} disabled={completando}>
                {completando ? 'Guardando...' : 'Marcar completada'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* Sub-modal: selección de grupo */}
    {modalGrupoOpen && (
      <div className="modal-overlay" style={{ zIndex: 900 }} onMouseDown={e => e.target === e.currentTarget && setModalGrupoOpen(false)}>
        <div className="modal" style={{ width: 400 }}>
          <div className="modal-header">
            <div className="modal-title" style={{ fontSize: 15 }}>
              {tarea.grupo ? 'Cambiar grupo' : 'Agregar a grupo'}
            </div>
            <button className="modal-close" onClick={() => setModalGrupoOpen(false)}>✕</button>
          </div>
          <div className="modal-body">
            {grupos.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  Grupos existentes
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
                  {grupos.map(g => (
                    <button
                      key={g}
                      onClick={() => handleAgregarGrupo(g)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '9px 14px', borderRadius: 8, cursor: 'pointer', border: 'none',
                        background: tarea.grupo === g ? 'var(--blue-light)' : 'var(--surface2)',
                        color: tarea.grupo === g ? 'var(--blue)' : 'var(--text)',
                        fontWeight: tarea.grupo === g ? 600 : 400,
                        fontSize: 14, textAlign: 'left',
                        outline: tarea.grupo === g ? '1.5px solid var(--blue)' : '1px solid var(--border)',
                      }}
                    >
                      <span>📁</span>
                      <span style={{ flex: 1 }}>{g}</span>
                      {tarea.grupo === g && <span style={{ fontSize: 12 }}>✓ actual</span>}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                  O crear nuevo grupo
                </div>
              </>
            )}
            {grupos.length === 0 && (
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                Crear nuevo grupo
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="form-input"
                value={nuevoGrupoInput}
                onChange={e => setNuevoGrupoInput(e.target.value)}
                placeholder="Nombre del grupo..."
                onKeyDown={e => e.key === 'Enter' && nuevoGrupoInput.trim() && handleAgregarGrupo(nuevoGrupoInput.trim())}
                style={{ flex: 1 }}
                autoFocus={grupos.length === 0}
              />
              <button
                className="btn btn-primary"
                onClick={() => handleAgregarGrupo(nuevoGrupoInput.trim())}
                disabled={!nuevoGrupoInput.trim()}
              >
                Crear
              </button>
            </div>
          </div>
          {tarea.grupo && (
            <div className="modal-footer" style={{ justifyContent: 'flex-start', paddingTop: 8 }}>
              <button
                className="btn btn-danger"
                onClick={() => handleAgregarGrupo(null)}
              >
                ✕ Quitar del grupo actual
              </button>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}
